/**
 * ⚙️ Workflow Editor — editor visual de nodos del workflow
 * Usa un layout lineal/secuencial con Polaris (sin React Flow).
 * Los nodos se muestran como cards conectadas verticalmente.
 * Ruta: /apps/workflows/:workflowId
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Modal,
  TextField,
  Select,
  Badge,
  Box,
  Spinner,
  Banner,
  Divider,
  Icon,
} from '@/lib/polaris';
import {
  AutomationIcon,
  PlusIcon,
  DeleteIcon,
  ArrowDownIcon,
  PlayIcon,
  PauseCircleIcon,
  ClockIcon,
  EmailIcon,
  EditIcon,
  PersonIcon,
} from '@/lib/polaris-icons';
import { PageBreadcrumbTitle } from '@/components/polaris/PageBreadcrumbTitle';
import { ROUTES } from '@/config/routes.config';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@smarty/shared';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { useParams } from 'react-router-dom';

// ─── Types ──────────────────────────────────────────────────
interface WorkflowNode {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: Record<string, unknown>;
}

interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  label?: string;
}

interface Workflow {
  id: string;
  name: string;
  description: string | null;
  status: string;
  trigger_type: string;
  trigger_config: Record<string, unknown>;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  enrollment_count: number;
  completed_count: number;
  error_count: number;
}

// ─── Node type config ───────────────────────────────────────
const NODE_TYPES: Record<string, { label: string; category: string; icon: typeof EmailIcon; color: string }> = {
  trigger: { label: 'Trigger', category: 'control', icon: PlayIcon, color: 'bg-fill-success' },
  delay: { label: 'Esperar (delay)', category: 'control', icon: ClockIcon, color: 'bg-fill-warning' },
  condition: { label: 'Condición (if/else)', category: 'control', icon: AutomationIcon, color: 'bg-fill-info' },
  send_email: { label: 'Enviar email', category: 'action', icon: EmailIcon, color: 'bg-fill-brand' },
  update_field: { label: 'Actualizar campo', category: 'action', icon: EditIcon, color: 'bg-fill-brand' },
  create_task: { label: 'Crear tarea', category: 'action', icon: PlusIcon, color: 'bg-fill-brand' },
  add_tag: { label: 'Agregar etiqueta', category: 'action', icon: PlusIcon, color: 'bg-fill-brand' },
  remove_tag: { label: 'Quitar etiqueta', category: 'action', icon: DeleteIcon, color: 'bg-fill-critical' },
  assign_owner: { label: 'Asignar propietario', category: 'action', icon: PersonIcon, color: 'bg-fill-brand' },
  move_deal_stage: { label: 'Mover deal de etapa', category: 'action', icon: AutomationIcon, color: 'bg-fill-brand' },
  webhook: { label: 'Webhook externo', category: 'action', icon: AutomationIcon, color: 'bg-fill-secondary' },
  internal_notification: { label: 'Notificación interna', category: 'action', icon: EmailIcon, color: 'bg-fill-secondary' },
};

const ADD_NODE_OPTIONS = Object.entries(NODE_TYPES)
  .filter(([key]) => key !== 'trigger')
  .map(([value, config]) => ({ label: config.label, value }));

const TRIGGER_LABELS: Record<string, string> = {
  contact_created: 'Contacto creado',
  contact_updated: 'Contacto actualizado',
  deal_created: 'Deal creado',
  deal_stage_changed: 'Deal cambió etapa',
  deal_won: 'Deal ganado',
  deal_lost: 'Deal perdido',
  order_created: 'Pedido creado',
  order_paid: 'Pedido pagado',
  form_submitted: 'Formulario enviado',
  email_opened: 'Email abierto',
  email_clicked: 'Email clickeado',
  manual: 'Ejecución manual',
  scheduled: 'Programado (cron)',
  webhook_received: 'Webhook recibido',
};

// ─── Component ──────────────────────────────────────────────
export default function WorkflowEditorPage() {
  const { workflowId } = useParams<{ workflowId: string }>();
  const storeNavigate = useStoreNavigate();
  const { tenantId } = useAuth();
  const toast = useAdminToast();

  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Ordered nodes (linear sequence derived from edges)
  const [orderedNodes, setOrderedNodes] = useState<WorkflowNode[]>([]);

  // Add node dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addAfterNodeId, setAddAfterNodeId] = useState<string | null>(null);
  const [newNodeType, setNewNodeType] = useState('send_email');

  // Edit node dialog
  const [editingNode, setEditingNode] = useState<WorkflowNode | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [nodeData, setNodeData] = useState<Record<string, string>>({});

  // ─── Load workflow ────────────────────────────────────────
  const loadWorkflow = useCallback(async () => {
    if (!tenantId || !workflowId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('workflows')
      .select('*')
      .eq('id', workflowId)
      .single();
    if (data) {
      const wf = data as unknown as Workflow;
      setWorkflow(wf);
      setOrderedNodes(buildOrderedNodes(wf.nodes, wf.edges));
    }
    if (error) toast.error('Workflow no encontrado');
    setLoading(false);
  }, [tenantId, workflowId]);

  useEffect(() => {
    if (tenantId && workflowId) loadWorkflow();
  }, [tenantId, workflowId, loadWorkflow]);

  // ─── Build linear order from nodes/edges ──────────────────
  function buildOrderedNodes(nodes: WorkflowNode[], edges: WorkflowEdge[]): WorkflowNode[] {
    if (nodes.length === 0) return [];

    // Find trigger (root) node
    const triggerNode = nodes.find(n => n.type === 'trigger');
    if (!triggerNode) return nodes;

    const ordered: WorkflowNode[] = [triggerNode];
    const visited = new Set<string>([triggerNode.id]);
    let currentId = triggerNode.id;

    // Follow edges linearly
    while (true) {
      const edge = edges.find(e => e.source === currentId && !visited.has(e.target));
      if (!edge) break;
      const nextNode = nodes.find(n => n.id === edge.target);
      if (!nextNode) break;
      ordered.push(nextNode);
      visited.add(nextNode.id);
      currentId = nextNode.id;
    }

    // Add any orphan nodes
    for (const n of nodes) {
      if (!visited.has(n.id)) ordered.push(n);
    }

    return ordered;
  }

  // ─── Add node ─────────────────────────────────────────────
  const handleAddNode = () => {
    if (!workflow) return;
    const newId = `node-${Date.now()}`;
    const newNode: WorkflowNode = {
      id: newId,
      type: newNodeType,
      position: { x: 250, y: (orderedNodes.length + 1) * 150 },
      data: { label: NODE_TYPES[newNodeType]?.label || newNodeType },
    };

    const newNodes = [...workflow.nodes, newNode];
    let newEdges = [...workflow.edges];

    if (addAfterNodeId) {
      // Remove existing edge from addAfterNodeId
      const existingEdge = newEdges.find(e => e.source === addAfterNodeId);
      const nextTarget = existingEdge?.target;
      newEdges = newEdges.filter(e => e.source !== addAfterNodeId || e.target !== nextTarget);

      // Add edge: afterNode -> newNode
      newEdges.push({ id: `edge-${Date.now()}-1`, source: addAfterNodeId, target: newId });

      // If there was a next node, connect: newNode -> nextNode
      if (nextTarget) {
        newEdges.push({ id: `edge-${Date.now()}-2`, source: newId, target: nextTarget });
      }
    } else if (orderedNodes.length > 0) {
      // Append to end
      const lastNode = orderedNodes[orderedNodes.length - 1];
      newEdges.push({ id: `edge-${Date.now()}`, source: lastNode.id, target: newId });
    }

    const updated = { ...workflow, nodes: newNodes, edges: newEdges };
    setWorkflow(updated);
    setOrderedNodes(buildOrderedNodes(newNodes, newEdges));
    setDirty(true);
    setAddDialogOpen(false);
  };

  // ─── Remove node ──────────────────────────────────────────
  const handleRemoveNode = (nodeId: string) => {
    if (!workflow) return;
    // Don't remove trigger
    const node = workflow.nodes.find(n => n.id === nodeId);
    if (!node || node.type === 'trigger') return;

    // Find incoming and outgoing edges
    const inEdge = workflow.edges.find(e => e.target === nodeId);
    const outEdge = workflow.edges.find(e => e.source === nodeId);

    const newEdges = workflow.edges.filter(e => e.source !== nodeId && e.target !== nodeId);

    // Reconnect: predecessor -> successor
    if (inEdge && outEdge) {
      newEdges.push({ id: `edge-${Date.now()}`, source: inEdge.source, target: outEdge.target });
    }

    const newNodes = workflow.nodes.filter(n => n.id !== nodeId);
    const updated = { ...workflow, nodes: newNodes, edges: newEdges };
    setWorkflow(updated);
    setOrderedNodes(buildOrderedNodes(newNodes, newEdges));
    setDirty(true);
  };

  // ─── Edit node data ──────────────────────────────────────
  const openEditNode = (node: WorkflowNode) => {
    setEditingNode(node);
    const data: Record<string, string> = {};
    for (const [key, val] of Object.entries(node.data)) {
      data[key] = String(val ?? '');
    }
    setNodeData(data);
    setEditDialogOpen(true);
  };

  const handleSaveNodeData = () => {
    if (!workflow || !editingNode) return;
    const updatedNodes = workflow.nodes.map(n => {
      if (n.id === editingNode.id) {
        return { ...n, data: { ...n.data, ...nodeData } };
      }
      return n;
    });
    const updated = { ...workflow, nodes: updatedNodes, edges: workflow.edges };
    setWorkflow(updated);
    setOrderedNodes(buildOrderedNodes(updatedNodes, updated.edges));
    setDirty(true);
    setEditDialogOpen(false);
  };

  // ─── Save workflow ────────────────────────────────────────
  const handleSave = async () => {
    if (!workflow) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('workflows')
        .update({ nodes: workflow.nodes, edges: workflow.edges })
        .eq('id', workflow.id);
      if (error) throw error;
      toast.success('Workflow guardado');
      setDirty(false);
    } catch (error: unknown) {
      toast.error('Error al guardar', getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  // ─── Toggle status ────────────────────────────────────────
  const handleToggleStatus = async () => {
    if (!workflow) return;
    const newStatus = workflow.status === 'active' ? 'paused' : 'active';
    const { error } = await supabase
      .from('workflows')
      .update({ status: newStatus })
      .eq('id', workflow.id);
    if (error) {
      toast.error('Error al cambiar estado');
    } else {
      setWorkflow({ ...workflow, status: newStatus });
      toast.success(newStatus === 'active' ? 'Workflow activado' : 'Workflow pausado');
    }
  };

  // ─── Node config fields based on type ─────────────────────
  const renderNodeConfigFields = (type: string) => {
    switch (type) {
      case 'delay':
        return (
          <>
            <TextField label="Días" type="number" value={nodeData.days ?? '0'} onChange={(v) => setNodeData({ ...nodeData, days: v })} autoComplete="off" />
            <TextField label="Horas" type="number" value={nodeData.hours ?? '0'} onChange={(v) => setNodeData({ ...nodeData, hours: v })} autoComplete="off" />
            <TextField label="Minutos" type="number" value={nodeData.minutes ?? '0'} onChange={(v) => setNodeData({ ...nodeData, minutes: v })} autoComplete="off" />
          </>
        );
      case 'condition':
        return (
          <>
            <TextField label="Campo" value={nodeData.field ?? ''} onChange={(v) => setNodeData({ ...nodeData, field: v })} placeholder="status" autoComplete="off" />
            <Select
              label="Operador"
              options={[
                { label: 'Igual a', value: 'equals' },
                { label: 'No igual a', value: 'not_equals' },
                { label: 'Contiene', value: 'contains' },
                { label: 'Mayor que', value: 'greater_than' },
                { label: 'Menor que', value: 'less_than' },
                { label: 'Está vacío', value: 'is_empty' },
                { label: 'No está vacío', value: 'is_not_empty' },
              ]}
              value={nodeData.operator ?? 'equals'}
              onChange={(v) => setNodeData({ ...nodeData, operator: v })}
            />
            <TextField label="Valor" value={nodeData.value ?? ''} onChange={(v) => setNodeData({ ...nodeData, value: v })} autoComplete="off" />
          </>
        );
      case 'send_email':
        return (
          <>
            <TextField label="Asunto" value={nodeData.subject ?? ''} onChange={(v) => setNodeData({ ...nodeData, subject: v })} placeholder="Hola {{first_name}}" autoComplete="off" />
            <TextField label="Contenido HTML" value={nodeData.body_html ?? ''} onChange={(v) => setNodeData({ ...nodeData, body_html: v })} multiline={5} autoComplete="off" helpText="Usa {{campo}} para merge tags" />
            <TextField label="Template ID (opcional)" value={nodeData.template_id ?? ''} onChange={(v) => setNodeData({ ...nodeData, template_id: v })} autoComplete="off" />
          </>
        );
      case 'update_field':
        return (
          <>
            <TextField label="Campo a actualizar" value={nodeData.field ?? ''} onChange={(v) => setNodeData({ ...nodeData, field: v })} placeholder="status" autoComplete="off" />
            <TextField label="Nuevo valor" value={nodeData.value ?? ''} onChange={(v) => setNodeData({ ...nodeData, value: v })} autoComplete="off" />
          </>
        );
      case 'create_task':
        return (
          <>
            <TextField label="Título de la tarea" value={nodeData.title ?? ''} onChange={(v) => setNodeData({ ...nodeData, title: v })} autoComplete="off" />
            <TextField label="Descripción" value={nodeData.description ?? ''} onChange={(v) => setNodeData({ ...nodeData, description: v })} multiline={3} autoComplete="off" />
            <TextField label="Días para vencer" type="number" value={nodeData.due_days ?? '1'} onChange={(v) => setNodeData({ ...nodeData, due_days: v })} autoComplete="off" />
          </>
        );
      case 'add_tag':
      case 'remove_tag':
        return (
          <TextField label="Etiqueta" value={nodeData.tag ?? ''} onChange={(v) => setNodeData({ ...nodeData, tag: v })} placeholder="vip" autoComplete="off" />
        );
      case 'assign_owner':
        return (
          <TextField label="ID del propietario" value={nodeData.owner_id ?? ''} onChange={(v) => setNodeData({ ...nodeData, owner_id: v })} autoComplete="off" helpText="UUID del usuario a asignar" />
        );
      case 'move_deal_stage':
        return (
          <Select
            label="Nueva etapa"
            options={[
              { label: 'Nuevo', value: 'new' },
              { label: 'Contactado', value: 'contacted' },
              { label: 'Calificado', value: 'qualified' },
              { label: 'Propuesta', value: 'proposal' },
              { label: 'Negociación', value: 'negotiation' },
              { label: 'Ganado', value: 'won' },
              { label: 'Perdido', value: 'lost' },
            ]}
            value={nodeData.stage ?? 'contacted'}
            onChange={(v) => setNodeData({ ...nodeData, stage: v })}
          />
        );
      case 'webhook':
        return (
          <TextField label="URL del webhook" value={nodeData.url ?? ''} onChange={(v) => setNodeData({ ...nodeData, url: v })} placeholder="https://..." autoComplete="off" />
        );
      case 'internal_notification':
        return (
          <TextField label="Mensaje" value={nodeData.message ?? ''} onChange={(v) => setNodeData({ ...nodeData, message: v })} multiline={3} autoComplete="off" />
        );
      default:
        return <Text as="p" tone="subdued">Sin configuración adicional.</Text>;
    }
  };

  // ─── Render ───────────────────────────────────────────────
  if (loading) {
    return (
      <Page title="Cargando…">
        <Card><Box padding="800"><InlineStack align="center"><Spinner size="large" /></InlineStack></Box></Card>
      </Page>
    );
  }

  if (!workflow) {
    return (
      <Page title="Workflow no encontrado">
        <Banner tone="critical"><p>El workflow no existe o no tienes acceso.</p></Banner>
      </Page>
    );
  }

  return (
    <Page
      title={
        <PageBreadcrumbTitle
          icon={AutomationIcon}
          title={workflow.name}
          onBack={() => storeNavigate(ROUTES.APPS_WORKFLOWS)}
          backAccessibilityLabel="Volver a workflows"
        />
      }
      primaryAction={{
        content: 'Guardar',
        onAction: handleSave,
        loading: saving,
        disabled: !dirty,
      }}
      secondaryActions={[
        {
          content: workflow.status === 'active' ? 'Pausar' : 'Activar',
          icon: workflow.status === 'active' ? PauseCircleIcon : PlayIcon,
          onAction: handleToggleStatus,
        },
      ]}
    >
      <BlockStack gap="400">
        {/* Stats banner */}
        <Card padding="300">
          <InlineStack gap="400" blockAlign="center" wrap>
            <Badge tone={workflow.status === 'active' ? 'success' : 'warning'}>
              {workflow.status === 'active' ? 'Activo' : workflow.status === 'paused' ? 'Pausado' : 'Borrador'}
            </Badge>
            <Text as="span" tone="subdued" variant="bodySm">
              Trigger: {TRIGGER_LABELS[workflow.trigger_type] ?? workflow.trigger_type}
            </Text>
            <Text as="span" tone="subdued" variant="bodySm">
              {workflow.enrollment_count} enrollments · {workflow.completed_count} completados · {workflow.error_count} errores
            </Text>
            {dirty && <Badge tone="warning">Sin guardar</Badge>}
          </InlineStack>
        </Card>

        {/* Flow Editor — nodos secuenciales */}
        <Card>
          <BlockStack gap="0">
            {orderedNodes.map((node, idx) => {
              const config = NODE_TYPES[node.type];
              return (
                <div key={node.id}>
                  {/* Connector arrow */}
                  {idx > 0 && (
                    <Box padding="200">
                      <InlineStack align="center">
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <div style={{ width: 2, height: 24, background: 'var(--p-color-border)' }} />
                          <Icon source={ArrowDownIcon} tone="subdued" />
                          <Button
                            variant="plain"
                            size="micro"
                            icon={PlusIcon}
                            onClick={() => { setAddAfterNodeId(orderedNodes[idx - 1].id); setAddDialogOpen(true); }}
                            accessibilityLabel="Insertar nodo"
                          />
                        </div>
                      </InlineStack>
                    </Box>
                  )}

                  {/* Node card */}
                  <Box
                    padding="400"
                    background="bg-surface-secondary"
                    borderRadius="200"
                    borderWidth="025"
                    borderColor="border"
                  >
                    <InlineStack align="space-between" blockAlign="center">
                      <InlineStack gap="300" blockAlign="center">
                        <Icon source={config?.icon ?? AutomationIcon} />
                        <BlockStack gap="050">
                          <Text as="span" fontWeight="semibold">
                            {config?.label ?? node.type}
                          </Text>
                          {node.type === 'trigger' && (
                            <Text as="span" tone="subdued" variant="bodySm">
                              {TRIGGER_LABELS[workflow.trigger_type] ?? workflow.trigger_type}
                            </Text>
                          )}
                          {node.type === 'delay' && (
                            <Text as="span" tone="subdued" variant="bodySm">
                              {node.data.days ? `${node.data.days}d ` : ''}{node.data.hours ? `${node.data.hours}h ` : ''}{node.data.minutes ? `${node.data.minutes}m` : ''}
                            </Text>
                          )}
                          {node.type === 'send_email' && node.data.subject && (
                            <Text as="span" tone="subdued" variant="bodySm">Asunto: {String(node.data.subject)}</Text>
                          )}
                          {node.type === 'condition' && node.data.field && (
                            <Text as="span" tone="subdued" variant="bodySm">
                              Si {String(node.data.field)} {String(node.data.operator)} {String(node.data.value ?? '')}
                            </Text>
                          )}
                          {node.type === 'update_field' && node.data.field && (
                            <Text as="span" tone="subdued" variant="bodySm">
                              {String(node.data.field)} = {String(node.data.value ?? '')}
                            </Text>
                          )}
                          {node.type === 'create_task' && node.data.title && (
                            <Text as="span" tone="subdued" variant="bodySm">{String(node.data.title)}</Text>
                          )}
                          {(node.type === 'add_tag' || node.type === 'remove_tag') && node.data.tag && (
                            <Text as="span" tone="subdued" variant="bodySm">Etiqueta: {String(node.data.tag)}</Text>
                          )}
                          {node.type === 'move_deal_stage' && node.data.stage && (
                            <Text as="span" tone="subdued" variant="bodySm">→ {String(node.data.stage)}</Text>
                          )}
                        </BlockStack>
                      </InlineStack>
                      <InlineStack gap="100">
                        {node.type !== 'trigger' && (
                          <>
                            <Button icon={EditIcon} variant="plain" size="slim" onClick={() => openEditNode(node)} accessibilityLabel="Configurar" />
                            <Button icon={DeleteIcon} variant="plain" size="slim" tone="critical" onClick={() => handleRemoveNode(node.id)} accessibilityLabel="Eliminar" />
                          </>
                        )}
                      </InlineStack>
                    </InlineStack>
                  </Box>
                </div>
              );
            })}

            {/* Add node at end */}
            <Box padding="300">
              <InlineStack align="center">
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ width: 2, height: 24, background: 'var(--p-color-border)' }} />
                  <Button
                    icon={PlusIcon}
                    onClick={() => { setAddAfterNodeId(orderedNodes[orderedNodes.length - 1]?.id ?? null); setAddDialogOpen(true); }}
                    variant="secondary"
                    size="slim"
                  >
                    Agregar paso
                  </Button>
                </div>
              </InlineStack>
            </Box>
          </BlockStack>
        </Card>
      </BlockStack>

      {/* Modal agregar nodo */}
      <Modal
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        title="Agregar paso al workflow"
        primaryAction={{ content: 'Agregar', onAction: handleAddNode }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => setAddDialogOpen(false) }]}
      >
        <Modal.Section>
          <Select
            label="Tipo de acción"
            options={ADD_NODE_OPTIONS}
            value={newNodeType}
            onChange={setNewNodeType}
          />
        </Modal.Section>
      </Modal>

      {/* Modal editar nodo */}
      <Modal
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title={`Configurar: ${NODE_TYPES[editingNode?.type ?? '']?.label ?? editingNode?.type}`}
        primaryAction={{ content: 'Guardar', onAction: handleSaveNodeData }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => setEditDialogOpen(false) }]}
        large
      >
        <Modal.Section>
          <BlockStack gap="400">
            {editingNode && renderNodeConfigFields(editingNode.type)}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
